import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ErrorInfo } from '../hooks/useErrorHandler';

export interface GlobalError {
  id: string;
  message: string;
  code?: string;
  details?: any;
  timestamp: Date;
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: string;
  retryable: boolean;
  dismissed: boolean;
}

export interface ErrorContextType {
  errors: GlobalError[];
  addError: (error: Omit<GlobalError, 'id' | 'timestamp' | 'dismissed'>) => string;
  dismissError: (id: string) => void;
  dismissAllErrors: () => void;
  clearErrors: () => void;
  getErrorsBySeverity: (severity: GlobalError['severity']) => GlobalError[];
  hasErrors: boolean;
  hasCriticalErrors: boolean;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useErrorContext = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useErrorContext must be used within an ErrorProvider');
  }
  return context;
};

interface ErrorProviderProps {
  children: ReactNode;
  maxErrors?: number;
  onError?: (error: GlobalError) => void;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({
  children,
  maxErrors = 50,
  onError
}) => {
  const [errors, setErrors] = useState<GlobalError[]>([]);

  const addError = useCallback((errorData: Omit<GlobalError, 'id' | 'timestamp' | 'dismissed'>) => {
    const id = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const error: GlobalError = {
      ...errorData,
      id,
      timestamp: new Date(),
      dismissed: false
    };

    setErrors(prev => {
      const newErrors = [error, ...prev].slice(0, maxErrors);
      return newErrors;
    });

    if (onError) {
      onError(error);
    }

    // Log critical errors to console
    if (error.severity === 'critical') {
      console.error('Critical error:', error);
    }

    return id;
  }, [maxErrors, onError]);

  const dismissError = useCallback((id: string) => {
    setErrors(prev => prev.map(error => 
      error.id === id ? { ...error, dismissed: true } : error
    ));
  }, []);

  const dismissAllErrors = useCallback(() => {
    setErrors(prev => prev.map(error => ({ ...error, dismissed: true })));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const getErrorsBySeverity = useCallback((severity: GlobalError['severity']) => {
    return errors.filter(error => error.severity === severity && !error.dismissed);
  }, [errors]);

  const hasErrors = errors.some(error => !error.dismissed);
  const hasCriticalErrors = errors.some(error => error.severity === 'critical' && !error.dismissed);

  const value: ErrorContextType = {
    errors,
    addError,
    dismissError,
    dismissAllErrors,
    clearErrors,
    getErrorsBySeverity,
    hasErrors,
    hasCriticalErrors
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};

// Hook for handling API errors
export const useApiErrorHandler = () => {
  const { addError } = useErrorContext();

  const handleApiError = useCallback((error: any, source: string = 'API') => {
    let severity: GlobalError['severity'] = 'medium';
    let message = 'An unexpected error occurred';
    let code: string | undefined;
    let retryable = true;

    // Determine severity and message based on error type
    if (error.response) {
      // HTTP error response
      const status = error.response.status;
      code = `HTTP_${status}`;

      switch (status) {
        case 400:
          message = 'Invalid request. Please check your input.';
          severity = 'low';
          retryable = false;
          break;
        case 401:
          message = 'Authentication required. Please log in again.';
          severity = 'high';
          retryable = false;
          break;
        case 403:
          message = 'Access denied. You don\'t have permission to perform this action.';
          severity = 'high';
          retryable = false;
          break;
        case 404:
          message = 'The requested resource was not found.';
          severity = 'low';
          retryable = false;
          break;
        case 429:
          message = 'Too many requests. Please wait a moment and try again.';
          severity = 'medium';
          retryable = true;
          break;
        case 500:
          message = 'Server error. Please try again later.';
          severity = 'high';
          retryable = true;
          break;
        case 502:
        case 503:
        case 504:
          message = 'Service temporarily unavailable. Please try again later.';
          severity = 'high';
          retryable = true;
          break;
        default:
          message = `Server error (${status}). Please try again.`;
          severity = 'medium';
          retryable = true;
      }
    } else if (error.request) {
      // Network error
      message = 'Network error. Please check your connection and try again.';
      severity = 'high';
      retryable = true;
      code = 'NETWORK_ERROR';
    } else {
      // Other error
      message = error.message || 'An unexpected error occurred';
      severity = 'medium';
      retryable = true;
    }

    addError({
      message,
      code,
      details: {
        originalError: error,
        source,
        timestamp: new Date().toISOString()
      },
      severity,
      source,
      retryable
    });
  }, [addError]);

  return { handleApiError };
};

// Hook for handling validation errors
export const useValidationErrorHandler = () => {
  const { addError } = useErrorContext();

  const handleValidationError = useCallback((errors: Record<string, string[]>, source: string = 'Validation') => {
    Object.entries(errors).forEach(([field, messages]) => {
      messages.forEach(message => {
        addError({
          message: `${field}: ${message}`,
          code: 'VALIDATION_ERROR',
          details: { field, message },
          severity: 'low',
          source,
          retryable: false
        });
      });
    });
  }, [addError]);

  return { handleValidationError };
};

// Hook for handling Firebase errors
export const useFirebaseErrorHandler = () => {
  const { addError } = useErrorContext();

  const handleFirebaseError = useCallback((error: any, source: string = 'Firebase') => {
    let message = 'Database error occurred';
    let severity: GlobalError['severity'] = 'medium';
    let retryable = true;

    // Map Firebase error codes to user-friendly messages
    switch (error.code) {
      case 'auth/user-not-found':
        message = 'User account not found. Please check your credentials.';
        severity = 'low';
        retryable = false;
        break;
      case 'auth/wrong-password':
        message = 'Incorrect password. Please try again.';
        severity = 'low';
        retryable = false;
        break;
      case 'auth/email-already-in-use':
        message = 'This email is already registered. Please use a different email.';
        severity = 'low';
        retryable = false;
        break;
      case 'auth/weak-password':
        message = 'Password is too weak. Please choose a stronger password.';
        severity = 'low';
        retryable = false;
        break;
      case 'auth/invalid-email':
        message = 'Invalid email address. Please check your email format.';
        severity = 'low';
        retryable = false;
        break;
      case 'permission-denied':
        message = 'Permission denied. You don\'t have access to this resource.';
        severity = 'high';
        retryable = false;
        break;
      case 'unavailable':
        message = 'Service temporarily unavailable. Please try again later.';
        severity = 'high';
        retryable = true;
        break;
      case 'deadline-exceeded':
        message = 'Request timed out. Please try again.';
        severity = 'medium';
        retryable = true;
        break;
      default:
        message = error.message || 'Database error occurred';
        severity = 'medium';
        retryable = true;
    }

    addError({
      message,
      code: error.code,
      details: {
        originalError: error,
        source,
        timestamp: new Date().toISOString()
      },
      severity,
      source,
      retryable
    });
  }, [addError]);

  return { handleFirebaseError };
};
